import { z } from 'zod';

import { ProductAvailabilityStatus } from '../../../enums';

export const ShopProductToolNames = {
  GetOperator: 'getOperator',
  GetShopProducts: 'getShopProducts',
  GetShopProductByProductId: 'getShopProductByProductId',
  GetShopProductByExternalId: 'getShopProductByExternalId',
  GetShopProductByEan: 'getShopProductByEan',
  GetShopProductByIsbn: 'getShopProductByIsbn',
  GetShopCategories: 'getShopCategories',
  GetShopDeliveryOptions: 'getShopDeliveryOptions',
  GetShopProductAvailability: 'getShopProductAvailability',
  GetShopProductGifts: 'getShopProductGifts',
  GetShopProductAccessories: 'getShopProductAccessories',
  CompareShopProducts: 'compareShopProducts',
} as const;

/**
 * Schema for preview of a product. Used in arrays,
 * carousels, get accessories, etc.
 */
export const shopProductPreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().nullish(),
  price: z.number(),
  imageUrl: z.string().nullish(),
  url: z.string(),
  description: z.string().nullish(),
});

export type ShopProductPreview = z.infer<typeof shopProductPreviewSchema>;

/**
 * GetShopProducts tool contracts
 */
export const getShopProductsArgsSchema = z.object({
  query: z
    .string()
    .nullish()
    .describe(
      'Full, unmodified search query from the user. Do not simplify, shorten, or modify the search term as the entire query is used for semantic and keyword search. Example: "Supplements, with B or C vitamins" should be passed as-is.',
    ),
  categoryIds: z
    .array(z.string())
    .nullish()
    .describe(
      "Nullish array of category IDs to filter by. Use only when user specifically mentions categories or when refining broad searches. Caution: Using categoryIds can potentially limit results that might semantically match the query but aren't in specified categories. Prefer query-only for highly specific product searches.",
    ),
  minPrice: z
    .number()
    .nullish()
    .describe(
      'Minimum price filter. Only include when user explicitly mentions a price range or minimum price. Format: raw number without currency symbol (e.g., 500 not 500 Kč).',
    ),
  maxPrice: z
    .number()
    .nullish()
    .describe(
      'Maximum price filter. Only include when user explicitly mentions a price range or maximum price. Format: raw number without currency symbol (e.g., 1000 not 1000 Kč).',
    ),
  isOnSale: z
    .boolean()
    .nullish()
    .describe(
      'Filter by products on sale. Set to true only when user explicitly asks for discounted/sale items. Do not set this parameter unless specifically requested.',
    ),
  externalId: z.string().nullish().describe('External product ID'),
  ean: z.string().nullish().describe('EAN code of the product'),
  isbn: z.string().nullish().describe('ISBN code of the product'),
  sortBy: z
    .enum(['price', 'name'])
    .nullish()
    .describe(
      'Sort results by this field. Use "price" for price-sensitive queries, or "name" for alphabetical listings. Can only be used when `query` is not set.',
    ),
  sortOrder: z
    .enum(['asc', 'desc'])
    .nullish()
    .describe(
      'Sort direction. Use "asc" for lowest-to-highest price or A-Z naming. Use "desc" for highest-to-lowest price or Z-A naming.',
    ),
  attributes: z
    .array(
      z.object({
        name: z.string(),
        value: z.string(),
      }),
    )
    .nullish()
    .describe(
      'Product attributes to filter by (e.g., {name: "color", value: "red"}). Only use when user specifically mentions attribute values. Get valid attributes from availableFilters first.',
    ),
  limit: z
    .number()
    .nullish()
    .default(5)
    .describe(
      'Maximum number of products to return. Default is 10. Only change when user asks for more or fewer results.',
    ),
});

export const getShopProductsContentSchema = z.string();
export const getShopProductsArtifactSchema = z
  .object({
    totalCount: z.number(),
    hasMore: z.boolean(),
    products: z.array(shopProductPreviewSchema),
    categories: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          displayName: z.string().nullish(),
        }),
      )
      .nullish(),
    availableFilters: z
      .array(
        z.object({
          name: z.string(),
          displayName: z.string().nullish(),
          values: z.array(z.string()),
        }),
      )
      .nullish(),
  })
  .nullish();

export type GetShopProductsArgs = z.infer<typeof getShopProductsArgsSchema>;
export type GetShopProductsContent = z.infer<
  typeof getShopProductsContentSchema
>;
export type GetShopProductsArtifact = z.infer<
  typeof getShopProductsArtifactSchema
>;

/**
 * GetShopProduct tools by different identifiers
 * (productId, externalId, ean, isbn) contracts
 */
export const getShopProductByProductIdArgsSchema = z.object({
  productId: z.string().uuid().describe('The internal product ID (UUID).'),
});

export const getShopProductByExternalIdArgsSchema = z.object({
  externalId: z.string().describe('The external product ID.'),
});

export const getShopProductByEanArgsSchema = z.object({
  ean: z.string().describe('The EAN code of the product.'),
});

export const getShopProductByIsbnArgsSchema = z.object({
  isbn: z.string().describe('The ISBN code of the product.'),
});

/**
 * These are identical for all tools, so we can reuse the same schema.
 */
export const getShopProductContentSchema = z.string();
export const getShopProductArtifactSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string().nullish(),
  price: z.number(),
  brand: z.string().nullish(),
  description: z.string().nullish(),
  url: z.string(),
  imageUrl: z.string().nullish(),
  images: z.array(z.string()).nullish(),
  categories: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        displayName: z.string().nullish(),
      }),
    )
    .nullish(),
  gifts: z.array(z.string()).nullish(),
  accessories: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        title: z.string().nullish(),
        url: z.string(),
      }),
    )
    .nullish(),
  attributes: z.array(
    z.object({
      id: z.string(),
      value: z.string(),
      attribute: z.object({
        id: z.string(),
        displayName: z.string().nullish(),
        name: z.string(),
      }),
    }),
  ),
});

export type GetShopProductByProductIdArgs = z.infer<
  typeof getShopProductByProductIdArgsSchema
>;
export type GetShopProductByExternalIdArgs = z.infer<
  typeof getShopProductByExternalIdArgsSchema
>;
export type GetShopProductByEanArgs = z.infer<
  typeof getShopProductByEanArgsSchema
>;
export type GetShopProductByIsbnArgs = z.infer<
  typeof getShopProductByIsbnArgsSchema
>;

export type GetShopProductContent = z.infer<typeof getShopProductContentSchema>;
export type GetShopProductArtifact = z.infer<
  typeof getShopProductArtifactSchema
>;

/**
 * GetShopCategories tool contracts
 */
export const getShopCategoriesArgsSchema = z.object({
  parentCategoryId: z
    .string()
    .nullish()
    .describe('Nullish parent category ID to get subcategories.'),
});

export const shopCategoryPreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullish(),
});

export const getShopCategoriesContentSchema = z.string();
export const getShopCategoriesArtifactSchema = z.object({
  categories: z.array(
    shopCategoryPreviewSchema.merge(
      z.object({
        children: z.array(shopCategoryPreviewSchema).nullish(),
      }),
    ),
  ),
});

export type ShopCategoryPreview = z.infer<typeof shopCategoryPreviewSchema>;
export type GetShopCategoriesArgs = z.infer<typeof getShopCategoriesArgsSchema>;
export type GetShopCategoriesContent = z.infer<
  typeof getShopCategoriesContentSchema
>;
export type GetShopCategoriesArtifact = z.infer<
  typeof getShopCategoriesArtifactSchema
>;

/**
 * GetShopDeliveryOptions tool contracts
 */
export const getShopDeliveryOptionsArgsSchema = z.object({
  productId: z.string().describe('The product ID to get delivery options for'),
});

export const getShopDeliveryOptionsContentSchema = z.string();
export const getShopDeliveryOptionsArtifactSchema = z.object({
  productId: z.string(),
  deliveryDate: z.string().nullish(),
  deliveryOptions: z
    .array(
      z.object({
        carrierId: z.string(),
        price: z.number().nullish(),
        priceCod: z.number().nullish(),
      }),
    )
    .nullish(),
});

export type GetShopDeliveryOptionsArgs = z.infer<
  typeof getShopDeliveryOptionsArgsSchema
>;
export type GetShopDeliveryOptionsContent = z.infer<
  typeof getShopDeliveryOptionsContentSchema
>;
export type GetShopDeliveryOptionsArtifact = z.infer<
  typeof getShopDeliveryOptionsArtifactSchema
>;

/**
 * GetShopProductAvailability tool contracts
 */
export const getShopProductAvailabilityArgsSchema = z.object({
  productId: z.string().describe('The product ID to get delivery options for'),
});

export const getShopProductAvailabilityContentSchema = z.string();
export const getShopProductAvailabilityArtifactSchema = z.object({
  productId: z.string(),
  stockQuantity: z.number().int().nullish(),
  availability: z.nativeEnum(ProductAvailabilityStatus).nullish(),
});

export type GetShopProductAvailabilityArgs = z.infer<
  typeof getShopProductAvailabilityArgsSchema
>;
export type GetShopProductAvailabilityContent = z.infer<
  typeof getShopProductAvailabilityContentSchema
>;
export type GetShopProductAvailabilityArtifact = z.infer<
  typeof getShopProductAvailabilityArtifactSchema
>;

/**
 * GetShopProductGifts tool contracts
 */
export const getShopProductGiftsArgsSchema = z.object({
  productId: z.string().describe('The product ID to get gifts for'),
});

export const getShopProductGiftsContentSchema = z.string();
export const getShopProductGiftsArtifactSchema = z.object({
  productId: z.string(),
  gifts: z.array(z.string()),
});

export type GetShopProductGiftsArgs = z.infer<
  typeof getShopProductGiftsArgsSchema
>;
export type GetShopProductGiftsContent = z.infer<
  typeof getShopProductGiftsContentSchema
>;
export type GetShopProductGiftsArtifact = z.infer<
  typeof getShopProductGiftsArtifactSchema
>;

/**
 * GetShopProductAccessories tool contracts
 */
export const getShopProductAccessoriesArgsSchema = z.object({
  productId: z.string().describe('The product ID to get accessories for'),
});

export const getShopProductAccessoriesContentSchema = z.string();
export const getShopProductAccessoriesArtifactSchema = z.object({
  productId: z.string(),
  accessories: z.array(shopProductPreviewSchema),
});

export type GetShopProductAccessoriesArgs = z.infer<
  typeof getShopProductAccessoriesArgsSchema
>;
export type GetShopProductAccessoriesContent = z.infer<
  typeof getShopProductAccessoriesContentSchema
>;
export type GetShopProductAccessoriesArtifact = z.infer<
  typeof getShopProductAccessoriesArtifactSchema
>;

/**
 * GetOperator tool contracts
 */
export const getOperatorContentSchema = z.string();
export const getOperatorArtifactSchema = z.object({
  operator: z
    .object({
      id: z.string(),
      firstName: z.string().nullish(),
      lastName: z.string().nullish(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
    })
    .nullish(),
});

export type GetOperatorArtifact = z.infer<typeof getOperatorArtifactSchema>;
export type GetOperatorContent = z.infer<typeof getOperatorContentSchema>;

/**
 * Shop product comparison tool contracts
 */
export const compareShopProductsArgsSchema = z.object({
  productIds: z
    .array(z.string())
    .min(2)
    .max(5)
    .describe('Array of product IDs to compare. Min 2, max 5 products.'),
  highlightDifferences: z
    .boolean()
    .nullish()
    .default(true)
    .describe(
      'If true, the comparison will focus on highlighting differences between products.',
    ),
});

export const compareShopProductsContentSchema = z.string();
export const compareShopProductsArtifactSchema = z.object({
  products: z.array(getShopProductArtifactSchema),
  comparisonMatrix: z.array(
    z.object({
      attribute: z.string().describe('Name of the compared attribute'),
      displayName: z
        .string()
        .nullish()
        .describe('Human-readable name of the attribute'),
      values: z.array(
        z.object({
          productId: z.string(),
          value: z.string().nullish(),
          isDifferent: z.boolean().nullish(),
        }),
      ),
      isDifferent: z.boolean().nullish(),
    }),
  ),
});

export type CompareShopProductsArgs = z.infer<
  typeof compareShopProductsArgsSchema
>;
export type CompareShopProductsContent = z.infer<
  typeof compareShopProductsContentSchema
>;
export type CompareShopProductsArtifact = z.infer<
  typeof compareShopProductsArtifactSchema
>;
